import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2 } from "lucide-react";
import type { Application } from "@/hooks/useBioNexusData";
import type { TablesUpdate } from "@/integrations/supabase/types";
import { cleanTranslations, TranslationField, TranslationFields } from "./TranslationFields";

const TRANSLATION_FIELDS: TranslationField[] = [
  { locale: "zh", key: "description", label: "Description", type: "textarea" },
  { locale: "zh", key: "technical_brief", label: "Technical brief", type: "textarea" },
  { locale: "zh", key: "impact_brief", label: "Impact brief", type: "textarea" },
  { locale: "zh", key: "key_features", label: "Key features", type: "lines" },
  { locale: "it", key: "title", label: "Title", type: "text" },
  { locale: "it", key: "description", label: "Description", type: "textarea" },
  { locale: "it", key: "technical_brief", label: "Technical brief", type: "textarea" },
  { locale: "it", key: "impact_brief", label: "Impact brief", type: "textarea" },
  { locale: "it", key: "key_features", label: "Key features", type: "lines" },
];

interface ApplicationEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  application: Application | null;
  onSave: (id: string, updates: TablesUpdate<"applications">) => void;
  isSaving?: boolean;
}

export const ApplicationEditDialog = ({
  open,
  onOpenChange,
  application,
  onSave,
  isSaving,
}: ApplicationEditDialogProps) => {
  const [formData, setFormData] = useState<Record<string, unknown>>({});

  useEffect(() => {
    if (application) {
      setFormData({ ...application });
    }
  }, [application]);

  const updateField = (key: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const updateImageUrl = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      image_url: value,
      image_credit: "",
      image_source_url: "",
    }));
  };

  const handleSave = () => {
    if (!application) return;
    const updates: TablesUpdate<"applications"> = {
      title: formData.title as string,
      description: formData.description as string,
      category: formData.category as string,
      application_type: formData.application_type as string,
      image_url: (formData.image_url as string) || null,
      image_credit: (formData.image_credit as string) || null,
      image_source_url: (formData.image_source_url as string) || null,
      source_url: formData.source_url as string,
      status: formData.status as string,
      translations: cleanTranslations(formData.translations),
    };
    onSave(application.id, updates);
  };

  const fields = [
    { key: "title", label: "Title", type: "text" },
    { key: "description", label: "Description", type: "textarea" },
    { key: "category", label: "Category", type: "text" },
    { key: "application_type", label: "Application Type", type: "text" },
    { key: "image_url", label: "Image URL", type: "text" },
    { key: "image_credit", label: "Image Credit", type: "text" },
    { key: "image_source_url", label: "Image Source URL", type: "text" },
    { key: "source_url", label: "Source URL", type: "text" },
  ];

  if (!application) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display">
            Edit Application: {(formData.title as string) || application.title}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-4">
            {fields.map((field) => (
              <div key={field.key} className="space-y-2">
                <Label htmlFor={field.key} className="font-mono text-xs uppercase">
                  {field.label}
                </Label>
                {field.type === "textarea" ? (
                  <Textarea
                    id={field.key}
                    value={(formData[field.key] as string) || ""}
                    onChange={(e) => updateField(field.key, e.target.value)}
                    rows={3}
                  />
                ) : (
                  <Input
                    id={field.key}
                    value={(formData[field.key] as string) || ""}
                    onChange={(e) => field.key === "image_url"
                      ? updateImageUrl(e.target.value)
                      : updateField(field.key, e.target.value)}
                  />
                )}
              </div>
            ))}

            <TranslationFields
              fields={TRANSLATION_FIELDS}
              value={formData.translations}
              onChange={(next) => updateField("translations", next)}
            />
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
